import React, { useEffect } from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  id: string;
  title: string;
  message?: string;
  type: ToastType;
  duration?: number;
  onClose: (id: string) => void;
}

const typeConfig = {
  success: {
    icon: CheckCircle,
    bgColor: 'bg-[#BBE0EF]', // Light Blue for success/positive
    borderColor: 'border-[#161E54]',
    textColor: 'text-[#161E54]',
    iconColor: 'text-[#161E54]',
  },
  error: {
    icon: AlertTriangle,
    bgColor: 'bg-[#FF986A]', // Salmon for error/attention
    borderColor: 'border-[#161E54]',
    textColor: 'text-[#161E54]',
    iconColor: 'text-[#161E54]',
  },
  warning: {
    icon: AlertCircle,
    bgColor: 'bg-yellow-100', // Keep standard yellow for warning or maybe Orange?
    borderColor: 'border-[#161E54]',
    textColor: 'text-[#161E54]',
    iconColor: 'text-[#161E54]',
  },
  info: {
    icon: Info,
    bgColor: 'bg-white',
    borderColor: 'border-[#161E54]',
    textColor: 'text-[#161E54]',
    iconColor: 'text-[#F16D34]', // Orange icon
  },
};

const Toast: React.FC<ToastProps> = ({
  id,
  title,
  message,
  type,
  duration = 5000,
  onClose,
}) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose(id);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [id, duration, onClose]);

  const config = typeConfig[type];
  const Icon = config.icon;

  return (
    <div
      className={`flex items-start gap-4 p-4 border-2 shadow-neo rounded-none min-w-[320px] max-w-md animate-in slide-in-from-right duration-300 pointer-events-auto relative ${config.bgColor} ${config.borderColor}`}
      role="alert"
    >
      <div className={`flex-shrink-0 mt-0.5 ${config.iconColor}`}>
        <Icon className="w-6 h-6 stroke-[2.5px]" />
      </div>
      
      <div className="flex-1 flex flex-col gap-1 pr-6">
        <div className={`text-base font-bold uppercase tracking-wide leading-tight ${config.textColor}`}>
          {title}
        </div>
        {message && (
          <div className={`text-sm font-medium opacity-90 leading-relaxed ${config.textColor}`}>
            {message}
          </div>
        )}
      </div>

      <button
        className={`absolute top-2 right-2 p-1 hover:bg-black/5 transition-colors ${config.textColor}`}
        onClick={() => onClose(id)}
        aria-label="Close notification"
      >
        <X className="w-5 h-5 stroke-[2.5px]" />
      </button>
    </div>
  );
};

export default Toast;
